import { useOrgMembers, useSetMemberRoles, useSetMemberStatus } from "@/api/members/useOrgMembers";
import {
  formatMemberSince,
  roleLabel,
  sortOrgRoles,
  type OrgMember,
} from "@/api/members/memberList";
import { ORG_MEMBERSHIP_ROLES, type OrgMembershipRole } from "@/auth/membershipRoles";
import {
  ROLE_DESCRIPTIONS,
  formatRoleDiff,
  isSoleActiveAdmin,
  membershipAction,
  needsOwnAdminRemovalConfirm,
  ownAdminRemovalMessage,
  roleCheckboxState,
  rolesChanged,
  toggleRole,
} from "@/auth/memberRoleEditor";
import { useAuth } from "@/auth/AuthContext";
import { useOrgPermissions } from "@/auth/orgPermissions";
import { routes } from "@/constants/routes";
import { useNetworkStatus } from "@/network/NetworkContext";
import { useTheme } from "@/theme/ThemeProvider";
import { ActionNotice } from "@/ui/components/ActionNotice";
import { AppButton } from "@/ui/components/AppButton";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { StatusChip } from "@/ui/components/StatusChip";
import { EmptyState, LoadingState } from "@/ui/components/StateViews";
import { confirmAction } from "@/ui/confirmAction";
import { useLocalSearchParams, useRouter } from "expo-router";
import { LockSimpleIcon } from "phosphor-react-native";
import { useEffect, useRef, useState } from "react";
import { Pressable, Text, View } from "react-native";

type Notice = { tone: "success" | "error"; text: string };

const OFFLINE_HINT =
  "Du bist offline. Mitgliederverwaltung ist nur online möglich.";

function param(value: string | string[] | undefined): string {
  if (typeof value === "string") return value;
  return value?.[0] ?? "";
}

function statusLabel(status: OrgMember["status"]): string {
  if (status === "disabled") return "Deaktiviert";
  if (status === "invited") return "Eingeladen";
  return "Aktiv";
}

export default function MemberDetailScreen() {
  const params = useLocalSearchParams<{ tenantID: string; profileId: string }>();
  const tenantID = param(params.tenantID);
  const profileId = param(params.profileId);
  const router = useRouter();
  const { theme } = useTheme();
  const { isOnline } = useNetworkStatus();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const permissions = useOrgPermissions(tenantID || undefined);
  const listQuery = useOrgMembers(
    !permissions.loading && permissions.canManageMembers ? tenantID : undefined,
  );
  const saveRoles = useSetMemberRoles(tenantID);
  const saveStatus = useSetMemberStatus(tenantID);
  const [draft, setDraft] = useState<OrgMembershipRole[] | null>(null);
  const [roleNotice, setRoleNotice] = useState<Notice | null>(null);
  const [statusNotice, setStatusNotice] = useState<Notice | null>(null);
  const actionLock = useRef(false);

  useEffect(() => {
    if (roleNotice?.tone !== "success") return;
    const timer = setTimeout(() => setRoleNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [roleNotice]);

  useEffect(() => {
    if (statusNotice?.tone !== "success") return;
    const timer = setTimeout(() => setStatusNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [statusNotice]);

  const back = () => router.replace(routes.tenant.members(tenantID) as never);
  const orgName = permissions.orgName?.trim() || "Verein";

  if (permissions.loading || (permissions.canManageMembers && listQuery.isLoading)) {
    return (
      <Screen title="Mitglied">
        <LoadingState message="Mitglieder werden geladen …" />
      </Screen>
    );
  }

  if (!permissions.canManageMembers) {
    return (
      <Screen title="Mitglied" scroll>
        <EmptyState
          title="Nur für Administration und Vorstand"
          message="Kontaktdaten und Rollenverwaltung sind der Administration und dem Vorstand vorbehalten."
          action={
            <AppButton onPress={back} style={{ minHeight: 44 }}>
              Zur Mitgliederliste
            </AppButton>
          }
        />
      </Screen>
    );
  }

  if (listQuery.error) {
    return (
      <Screen title="Mitglied" scroll>
        <View accessibilityLiveRegion="polite" style={{ gap: theme.space.sm }}>
          <Text style={{ color: theme.color.danger, fontWeight: "700" }}>
            Die Mitgliederliste konnte nicht geladen werden.
          </Text>
          <Text style={{ color: theme.color.muted }}>
            Bitte versuche es gleich noch einmal. Bleibt das Problem, wende dich an die Administration.
          </Text>
          <AppButton
            variant="secondary"
            onPress={() => void listQuery.refetch()}
            style={{ minHeight: 44, alignSelf: "flex-start" }}
          >
            Erneut laden
          </AppButton>
        </View>
      </Screen>
    );
  }

  const member = (listQuery.data ?? []).find((row) => row.profileId === profileId);
  if (!member) {
    return (
      <Screen title="Mitglied" scroll>
        <EmptyState
          title="Mitglied nicht gefunden"
          message={`Diese Person ist nicht (mehr) Mitglied in ${orgName}.`}
          action={
            <AppButton onPress={back} style={{ minHeight: 44 }}>
              Zur Mitgliederliste
            </AppButton>
          }
        />
      </Screen>
    );
  }

  const name = member.displayName?.trim() || "Unbenannt";
  const current = sortOrgRoles(member.roles);
  const selected = draft ?? current;
  const changed = rolesChanged(current, selected);
  const since = formatMemberSince(member.activatedAt ?? member.createdAt);
  const isSelf = Boolean(userId && userId === member.profileId);
  const sole = isSoleActiveAdmin(listQuery.data ?? [], member.profileId);
  const readOnly = !permissions.canWriteMemberChanges;
  const action = membershipAction({
    isSelf,
    status: member.status,
    targetHasAdmin: member.roles.includes("admin"),
    canWrite: permissions.canWriteMemberChanges,
    canChangeAdminMemberStatus: permissions.canChangeAdminMemberStatus,
  });
  const busy = saveRoles.isPending || saveStatus.isPending;

  const save = async () => {
    if (actionLock.current || !isOnline || !changed || readOnly || busy) return;
    actionLock.current = true;
    try {
      if (
        needsOwnAdminRemovalConfirm({
          isSelf,
          currentRoles: current,
          nextRoles: selected,
        })
      ) {
        const ok = await confirmAction({
          title: 'Eigene Rolle "Administration" entfernen?',
          message: ownAdminRemovalMessage(selected),
          confirmLabel: "Entfernen",
          cancelLabel: "Abbrechen",
        });
        if (!ok) return;
      }
      await saveRoles.mutateAsync({ membershipId: member.membershipId, roles: selected });
      setDraft(null);
      setRoleNotice({ tone: "success", text: "Rollen gespeichert." });
    } catch (error) {
      console.error("set_member_roles", error);
      setRoleNotice({
        tone: "error",
        text: error instanceof Error ? error.message : "Das hat nicht geklappt. Bitte versuche es erneut.",
      });
    } finally {
      actionLock.current = false;
    }
  };

  const changeStatus = async () => {
    if (actionLock.current || !isOnline || !action.enabled || busy) return;
    actionLock.current = true;
    setStatusNotice(null);
    try {
      const next = action.mode === "deactivate" ? "disabled" : "active";
      if (next === "disabled") {
        const ok = await confirmAction({
          title: `${name} deaktivieren?`,
          message: `${name} kann sich weiter anmelden, hat aber keinen Zugriff mehr auf ${orgName}, bis die Mitgliedschaft reaktiviert wird.`,
          confirmLabel: "Deaktivieren",
          cancelLabel: "Abbrechen",
        });
        if (!ok) return;
      }
      await saveStatus.mutateAsync({ membershipId: member.membershipId, status: next });
      setStatusNotice({
        tone: "success",
        text: next === "disabled" ? `${name} wurde deaktiviert.` : `${name} ist wieder aktiv.`,
      });
    } catch (error) {
      console.error("set_member_status", error);
      setStatusNotice({
        tone: "error",
        text: error instanceof Error ? error.message : "Das hat nicht geklappt. Bitte versuche es erneut.",
      });
    } finally {
      actionLock.current = false;
    }
  };

  const diff = formatRoleDiff(current, selected);

  return (
    <Screen title={name} scroll contentStyle={{ gap: theme.space.lg, paddingBottom: theme.space.xxl }}>
      <View style={{ gap: theme.space.xs }}>
        <Text style={{ color: theme.color.text, fontSize: theme.fontSize.h2, fontWeight: "800" }}>
          {name}
        </Text>
        <StatusChip label={statusLabel(member.status)} tone="neutral" />
        {since ? (
          <Text style={{ color: theme.color.muted }}>Mitglied seit {since}</Text>
        ) : null}
      </View>

      {!isOnline ? <Text style={{ color: theme.color.muted }}>{OFFLINE_HINT}</Text> : null}

      <SectionCard title="Kontakt" subtitle="Nur für Administration und Vorstand sichtbar.">
        <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>E-Mail</Text>
        <Text style={{ color: theme.color.text, fontWeight: "700" }}>{member.email ?? "—"}</Text>
      </SectionCard>

      <SectionCard title="Rollen im Verein">
        <Text style={{ color: theme.color.muted, marginBottom: theme.space.sm }}>
          Mehrfachauswahl · mindestens eine Rolle
        </Text>
        <View style={{ gap: theme.space.xs }}>
          {ORG_MEMBERSHIP_ROLES.map((role) => {
            const box = roleCheckboxState({
              role,
              selected,
              canChangeAdminRole: permissions.canChangeAdminRole,
              readOnly,
              soleActiveAdmin: sole,
              isSelf,
            });
            const locked = box.disabled;
            const labelColor = locked ? theme.color.buttonDisabledText : theme.color.text;
            return (
              <Pressable
                key={role}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: box.checked, disabled: locked || !isOnline }}
                accessibilityLabel={`${roleLabel(role)}, ${ROLE_DESCRIPTIONS[role]}${locked ? ", gesperrt" : ""}`}
                accessibilityHint={box.hint ?? undefined}
                disabled={locked || !isOnline || busy}
                hitSlop={8}
                onPress={() => {
                  setRoleNotice(null);
                  setDraft(toggleRole(selected, role));
                }}
                style={{
                  minHeight: 44,
                  paddingVertical: theme.space.sm,
                  paddingHorizontal: theme.space.sm,
                  borderRadius: theme.radius.md,
                  borderWidth: 1,
                  borderColor: locked ? theme.color.inputBorder : theme.color.border,
                  backgroundColor: locked ? theme.color.buttonDisabledBg : theme.color.surface,
                  gap: 2,
                }}
              >
                <View style={{ flexDirection: "row", alignItems: "center", gap: theme.space.xs }}>
                  <Text style={{ color: labelColor, fontWeight: "700", flex: 1 }}>
                    {box.checked ? "✓ " : "○ "}
                    {roleLabel(role)}
                  </Text>
                  {locked ? (
                    <View
                      accessibilityElementsHidden
                      importantForAccessibility="no-hide-descendants"
                      style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
                    >
                      <LockSimpleIcon size={16} color={theme.color.buttonDisabledText} />
                      <Text style={{ color: theme.color.buttonDisabledText, fontWeight: "700" }}>
                        gesperrt
                      </Text>
                    </View>
                  ) : null}
                </View>
                <Text style={{ color: labelColor }}>{ROLE_DESCRIPTIONS[role]}</Text>
                {box.hint ? (
                  <Text style={{ color: labelColor, fontSize: theme.fontSize.small }}>
                    {box.hint}
                  </Text>
                ) : null}
              </Pressable>
            );
          })}
        </View>
        {diff ? (
          <Text style={{ color: theme.color.text, marginTop: theme.space.sm, fontWeight: "600" }}>
            {diff}
          </Text>
        ) : null}
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.sm, marginTop: theme.space.md }}>
          <AppButton
            onPress={() => void save()}
            disabled={!changed || readOnly || !isOnline || busy}
            loading={saveRoles.isPending}
            style={{ minHeight: 44 }}
          >
            Rollen speichern
          </AppButton>
          <AppButton
            variant="secondary"
            onPress={() => {
              setDraft(null);
              setRoleNotice(null);
            }}
            disabled={!changed || busy}
            style={{ minHeight: 44 }}
          >
            Verwerfen
          </AppButton>
        </View>
        <ActionNotice notice={roleNotice} />
      </SectionCard>

      <SectionCard title="Mitgliedschaft">
        {action.mode === "hidden" ? (
          <Text style={{ color: theme.color.muted }}>
            {isSelf
              ? "Die eigene Mitgliedschaft bleibt hier unverändert."
              : "Einladungen werden über offene Einladungen verwaltet."}
          </Text>
        ) : (
          <View style={{ gap: theme.space.sm }}>
            {action.reason ? (
              <Text style={{ color: theme.color.muted }}>{action.reason}</Text>
            ) : null}
            <AppButton
              variant={action.mode === "deactivate" ? "danger" : "primary"}
              onPress={() => void changeStatus()}
              disabled={!action.enabled || !isOnline || busy}
              loading={saveStatus.isPending}
              style={{ minHeight: 44, alignSelf: "flex-start" }}
            >
              {action.mode === "deactivate"
                ? "Mitgliedschaft deaktivieren"
                : "Mitgliedschaft reaktivieren"}
            </AppButton>
            <ActionNotice notice={statusNotice} />
          </View>
        )}
      </SectionCard>

      <AppButton variant="secondary" onPress={back} style={{ minHeight: 44, alignSelf: "flex-start" }}>
        Zur Mitgliederliste
      </AppButton>
    </Screen>
  );
}
